import { getNumberOfUnitsInLot } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'

import { getCostContextAnalyticsProperties } from '@/buyer-portal/components/features/listing/helpers/getCostContextAnalyticsProperties'
import { useCalculatedAdditionalCharges } from '@/buyer-portal/components/features/listing/hooks/useCalculatedAdditionalCharges'
import { useTlcTotals } from '@/buyer-portal/components/features/listing/hooks/useTlcTotals'
import activeSubscriptionsQuery from '@/buyer-portal/queries/activeSubscriptionsQuery'
import { getUserMinBidAmountForAuction } from '@/helpers/getUserMinBidAmount'

/**
 * Cost-context properties (current_bid_usd/minimum_required_bid_usd/
 * bstock_fee_usd/total_landed_cost_usd), shared by every LDP analytics event
 * that carries them — `useTrackPageViewed`, `BidActions`,
 * and `QuickBidButtons` — so the derivation lives in exactly one place.
 *
 * Only meaningful for auction listings: offering (negotiation) listings have
 * no bid, and no offer amount exists until the buyer enters one in the form,
 * so all four properties are null for those rather than guessing at a basis.
 *
 * Fee/TLC basis is always the current bid (`auction.winningBidAmount`, or
 * `startPrice` before any bids exist) — never the buyer's not-yet-submitted
 * form amount, so the same click that opens a confirmation modal reports the
 * same number the buyer was shown before typing anything.
 */
export const useCostContextAnalyticsProperties = ({
  listing,
  selectedQuote,
}: {
  listing: OrchestratedListingDetails
  selectedQuote?: OrchestratedQuote | null
}) => {
  // Same cache-backed query the action column / BidFormProvider already
  // fetch, keyed by the listing's seller account — this never issues its
  // own network request.
  const subscriptionQuery = activeSubscriptionsQuery.useQuery(listing.accountId)
  const auction = listing.auction

  const costContextBasis = auction
    ? (auction.winningBidAmount ?? auction.startPrice ?? 0)
    : 0

  const additionalCharges = useCalculatedAdditionalCharges({
    buyersPremium: subscriptionQuery.data?.buyersPremium ?? 0,
    currentBidDollars: costContextBasis,
    selectedQuote,
  })

  const tlcTotals = useTlcTotals({
    additionalCharges,
    currency: listing.currency,
    listingId: listing._id,
    listingType: listing.listingType,
    msrpPrice: listing.lot.retailValue,
    selectedQuote,
    units: getNumberOfUnitsInLot(listing.lot) ?? 0,
  })

  return {
    /**
     * True while the seller-subscription fee lookup is still in flight.
     * Callers that fire on a deferred effect (e.g. a page-view event)
     * should wait for this before reporting — a subscription still in
     * flight would otherwise report a false bstock_fee_usd: 0 instead of
     * the true not-yet-available null. Callers that fire on a user click
     * (bid buttons) can't sensibly delay and may ignore this, matching how
     * `BidFormProvider` already falls back to a buyersPremium of 0 without
     * gating bid-button interactivity on this same query.
     */
    isLoading: subscriptionQuery.isLoading,
    properties: getCostContextAnalyticsProperties({
      currentBidUsd: auction?.winningBidAmount ?? null,
      minimumRequiredBidUsd: auction
        ? getUserMinBidAmountForAuction(auction)
        : null,
      bstockFeeCents: auction ? additionalCharges.bstockFee : null,
      totalLandedCostUsd: auction ? tlcTotals.totalLandedCostWithTax : null,
    }),
  }
}
